import { useState, type ReactNode } from "react";

import { Empty } from "./Panel";
import { useConsensusTaker } from "../hooks/useConsensus";
import type { ConsensusCellKey, ConsensusPriceBucket, ConsensusTakerReport as Report } from "../api/types";
import {
  barrierLines,
  cellAxes,
  cellCode,
  cellLabel,
  edgeTone,
  feeTone,
  meanPrice,
  ratio,
  roiWord,
  ruleWord,
  scopeWord,
  shareTone,
  signedUsd,
  stepWord,
  toneColor,
  usd,
  usdCents,
  zWord,
  type Tone,
} from "../lib/consensus";

/**
 * Отчёт о тейкерском сигнале котировки когорты — то, что оператор читает
 * ПЕРВЫМ. Пять вопросов в порядке чтения: барьеры, сколько окон подходило, где
 * можно было зайти тейкером, распределение цены входа, итог с комиссией.
 *
 * Каждая ступень воронки — знаменатель следующей, поэтому доли печатаются на
 * каждой, а не только внизу. Цвет у каждого числа по одному правилу (см.
 * lib/consensus.ts: edgeTone / shareTone / feeTone) и всегда с текстовой
 * пометкой рядом — цвет не единственный носитель смысла.
 *
 * Клетка ОДНА и по умолчанию главная: клетки вложены, и сумма по сетке покупала
 * бы одно окно до восемнадцати раз. Переключатель — чтобы посмотреть соседей,
 * а не чтобы выбрать лучшую: лучшая из 36 гипотез выглядит кромкой по
 * построению.
 */
export function ConsensusTakerReport({ hours }: { hours: number }) {
  // Пустая клетка — «главная»: ручка сама подставит её координаты. Три оси
  // выбираются порознь, а код клетки для ручки собирается из них.
  const [pick, setPick] = useState<{ bp: number; stake: number; agree: number } | null>(null);
  const cell = pick ? cellCode(pick.bp, pick.stake, pick.agree) : "";
  const q = useConsensusTaker(hours, cell);
  const r = q.data;

  if (!r) {
    return <Empty>{q.isError ? "отчёт тейкера не загрузился" : "загрузка…"}</Empty>;
  }
  const axes = cellAxes(r.cells);
  const main = r.cells.find((c) => c.main_cell);
  // Текущие координаты — из ответа: до первого выбора это главная клетка.
  const [curBp, curStake, curAgree] = r.cell.split("-").map(Number) as [number, number, number];
  const choose = (bp: number, stake: number, agree: number) => setPick({ bp, stake, agree });

  return (
    <div className="flex flex-col gap-3">
      <Matrix cells={r.cells} axes={axes} current={r.cell} onPick={choose} />

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-[var(--color-muted)]">клетка</span>
        <Axis
          label="уверенность"
          value={curBp}
          options={axes.levels}
          word={(v) => (v / 10000).toFixed(2)}
          onChange={(v) => choose(v, curStake, curAgree)}
        />
        <Axis
          label="ставка"
          value={curStake}
          options={axes.stakes}
          word={(v) => `$${v.toLocaleString("ru-RU")}`}
          onChange={(v) => choose(curBp, v, curAgree)}
        />
        <Axis
          label="подтверждений"
          value={curAgree}
          options={axes.agree}
          word={(v) => `N=${v}`}
          onChange={(v) => choose(curBp, curStake, v)}
        />
        {main && !r.main_cell && (
          <button
            type="button"
            className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-muted)] hover:text-[var(--color-fg)]"
            onClick={() => setPick(null)}
          >
            ★ к главной {cellLabel(main)}
          </button>
        )}
        <span
          className="rounded border border-[var(--color-border)] px-2 py-0.5 num text-[var(--color-accent)]"
          title="область отчёта и самого сигнала: чужие серии строк не рождают (CONSENSUS_ASSETS, CONSENSUS_WINDOWS)"
        >
          {scopeWord(r.asset, r.window)}
        </span>
        {!r.main_cell && (
          <span className="text-[var(--color-warn)]">
            не главная клетка: лучшая из 36 гипотез выглядит кромкой по построению
          </span>
        )}
      </div>

      <Barriers r={r} />
      <Funnel r={r} />
      <Prices buckets={r.buckets} />
      <Outcome r={r} />
    </div>
  );
}

/** Один селектор одной оси сетки. */
function Axis({
  label,
  value,
  options,
  word,
  onChange,
}: {
  label: string;
  value: number;
  options: number[];
  word: (v: number) => string;
  onChange: (v: number) => void;
}) {
  return (
    <label className="flex items-center gap-1">
      <span className="text-[var(--color-muted)]">{label}</span>
      <select
        className="rounded border border-[var(--color-border)] bg-[var(--color-panel)] px-2 py-0.5 text-xs num"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
      >
        {options.map((v) => (
          <option key={v} value={v}>
            {word(v)}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Матрица режимов: строки — уверенность × подтверждений, столбцы — ставка. В
 * ячейке винрейт, счёт и ROI тейкера; цвет — по запасу в сигмах, как везде в
 * отчёте. Клик выбирает клетку. Клетки ВЛОЖЕНЫ — читать по ячейкам, столбцы
 * не складывать; лучшая ячейка из 18 выглядит кромкой по построению, и звезда
 * стоит у той, что назначена главной до данных.
 */
function Matrix({
  cells,
  axes,
  current,
  onPick,
}: {
  cells: ConsensusCellKey[];
  axes: { levels: number[]; stakes: number[]; agree: number[] };
  current: string;
  onPick: (bp: number, stake: number, agree: number) => void;
}) {
  if (cells.length === 0) return null;
  const byCode = new Map(cells.map((c) => [c.code, c]));
  return (
    <Section label="матрица режимов: винрейт · счёт · ROI тейкера по клеткам (клик — выбрать)">
      <div className="overflow-x-auto">
        <table className="text-xs">
          <thead className="text-left text-[var(--color-muted)]">
            <tr>
              <th className="pb-1 pr-3 font-normal">уверенность / N</th>
              {axes.stakes.map((s) => (
                <th key={s} className="pb-1 pr-3 text-right font-normal num">
                  ставка ${s.toLocaleString("ru-RU")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {axes.levels.flatMap((bp) =>
              axes.agree.map((n) => (
                <tr key={`${bp}-${n}`} className="border-t border-[var(--color-border)]">
                  <td className="py-1 pr-3 num text-[var(--color-muted)]">
                    {(bp / 10000).toFixed(2)} · N={n}
                  </td>
                  {axes.stakes.map((s) => {
                    const c = byCode.get(cellCode(bp, s, n));
                    const code = cellCode(bp, s, n);
                    const selected = code === current;
                    if (!c || c.entries === 0) {
                      return (
                        <td key={s} className="py-1 pr-3 text-right">
                          <button
                            type="button"
                            className="w-full rounded border border-transparent px-2 py-1 text-right text-[var(--color-muted)] hover:border-[var(--color-border)]"
                            onClick={() => onPick(bp, s, n)}
                            title="входов тейкера ещё не было"
                          >
                            —{c?.main_cell ? " ★" : ""}
                          </button>
                        </td>
                      );
                    }
                    const tone = edgeTone(Number(c.pnl), c.price_z);
                    return (
                      <td key={s} className="py-1 pr-3 text-right">
                        <button
                          type="button"
                          onClick={() => onPick(bp, s, n)}
                          aria-pressed={selected}
                          className="w-full rounded border px-2 py-1 text-right num hover:border-[var(--color-accent)]"
                          style={{
                            borderColor: selected ? "var(--color-accent)" : "var(--color-border)",
                            color: toneColor(tone),
                          }}
                          title={`${c.won}/${c.entries} при безубытке ${meanPrice(c.price_sum, c.entries)}, ${zWord(c.price_z)}; потрачено ${usd(c.cost)}`}
                        >
                          {ratio(c.won, c.entries)} · {signedUsd(c.pnl)} · {roiWord(c.roi)}
                          {c.main_cell ? " ★" : ""}
                          <Mark tone={tone} />
                        </button>
                      </td>
                    );
                  })}
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

/** (1) По каким барьерам считали — из нот трейдера, не из кода интерфейса. */
function Barriers({ r }: { r: Report }) {
  const lines = barrierLines(r.barriers);
  return (
    <Section label="барьеры: по каким правилам родились строки">
      {!r.barriers_known ? (
        <p className="text-xs" style={{ color: "var(--color-warn)" }}>
          трейдер недоступен — барьеры неизвестны (это не «барьеров нет»)
        </p>
      ) : lines.length === 0 ? (
        <Empty>трейдер не сообщил барьеров котировки группы</Empty>
      ) : (
        <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-[auto_1fr]">
          {lines.map((l) => (
            <FragmentRow key={l.label} label={l.label} value={l.value} />
          ))}
        </dl>
      )}
    </Section>
  );
}

function FragmentRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-[var(--color-muted)]">{label}</dt>
      <dd className="num text-[var(--color-fg)]">{value}</dd>
    </>
  );
}

/** (2)+(3) Сколько окон подходило и в каких можно было зайти тейкером. */
function Funnel({ r }: { r: Report }) {
  const steps: { label: string; part: number; total: number; hint: string }[] = [
    {
      label: "окон за период",
      part: r.windows_total,
      total: r.windows_total,
      hint: "окна этой серии за срок по реестру окон, но не раньше первой строки журнала",
    },
    {
      label: "с сигналом группы",
      part: r.windows_signal,
      total: r.windows_total,
      hint: "окна, где котировка группы перешагнула нижний барьер хотя бы одной клетки",
    },
    {
      label: "в этой клетке",
      part: r.windows_cell,
      total: r.windows_signal,
      hint: "окна со строкой выбранной клетки, включая погашенные правилом",
    },
    {
      label: "зачётных строк",
      part: r.clean,
      total: r.rows,
      hint: "строки клетки, не погашенные правилом (зона цены, фора, стакан); без мейкерского плана — тоже зачётные с 2026-09-03",
    },
    {
      label: "можно было зайти тейкером",
      part: r.takeable,
      total: r.clean,
      hint: "в живом стакане нашлось предложение под $25 не дороже потолка",
    },
    {
      label: "сведено",
      part: r.resolved,
      total: r.takeable,
      hint: "исход окна подтверждён, счёт посчитан",
    },
  ];
  return (
    <Section label="воронка: сколько окон подходило и где можно было войти">
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {steps.map((s, i) => {
          const tone = i === 0 ? undefined : shareTone(s.part, s.total);
          return (
            <div key={s.label} className="rounded border border-[var(--color-border)] p-2" title={s.hint}>
              <dt className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">{s.label}</dt>
              <dd className="num text-lg" style={{ color: toneColor(tone) }}>
                {s.part}
                <Mark tone={tone} />
              </dd>
              {i > 0 && (
                <dd className="num text-[10px] text-[var(--color-muted)]">{stepWord(s.part, s.total)}</dd>
              )}
            </div>
          );
        })}
      </dl>
      {(r.rules.length > 0 || r.reasons.length > 0) && (
        <div className="mt-2 grid gap-2 text-xs sm:grid-cols-2">
          <CountList
            title="погашено правилом"
            items={r.rules.map((x) => ({ key: ruleWord(x.key), n: x.n }))}
            total={r.rows}
          />
          <CountList title="зачётные без цены тейкера" items={r.reasons} total={r.clean} />
        </div>
      )}
      {r.no_maker_plan > 0 && (
        <p className="mt-2 text-[10px] text-[var(--color-muted)]">
          из зачётных <span className="num text-[var(--color-fg)]">{r.no_maker_plan}</span> без мейкерского
          плана (по биду нечего взять): тейкер судится, мейкер — нет. До 2026-09-03 такие строки гасило
          правило «нечего взять», и в старых сутках их в зачёте не было.
        </p>
      )}
    </Section>
  );
}

function CountList({ title, items, total }: { title: string; items: { key: string; n: number }[]; total: number }) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="mb-1 text-[var(--color-muted)]">{title}</div>
      <ul className="flex flex-col gap-0.5">
        {items.map((x) => {
          // Съеденная доля — обратный тон: чем больше правило съело, тем краснее.
          const share = total > 0 ? x.n / total : 0;
          const tone: Tone | undefined = total <= 0 ? undefined : share >= 0.5 ? "bad" : share >= 0.2 ? "warn" : "good";
          return (
            <li key={x.key} className="flex justify-between gap-2">
              <span>{x.key}</span>
              <span className="num" style={{ color: toneColor(tone) }}>
                {stepWord(x.n, total)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** (4) Распределение цены входа: где тейкер реально платил, и что там вышло. */
function Prices({ buckets }: { buckets: ConsensusPriceBucket[] }) {
  const max = Math.max(1, ...buckets.map((b) => b.entries));
  return (
    <Section label="цена входа тейкера: распределение и результат по корзинам 0.05">
      {buckets.length === 0 ? (
        <Empty>приценённых строк ещё нет</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-[var(--color-muted)]">
              <tr>
                <th className="pb-1 font-normal">цена входа</th>
                <th className="pb-1 font-normal">входов</th>
                <th className="pb-1 text-right font-normal">угадано</th>
                <th className="pb-1 text-right font-normal">безубыток</th>
                <th className="pb-1 text-right font-normal">против цены</th>
                <th className="pb-1 text-right font-normal">комиссия</th>
                <th className="pb-1 text-right font-normal">счёт</th>
                <th className="pb-1 text-right font-normal">ROI</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b) => {
                const pnl = Number(b.pnl);
                const tone = edgeTone(pnl, b.price_z);
                const win = b.resolved > 0 ? b.won / b.resolved : null;
                const brk = b.resolved > 0 ? Number(b.price_sum) / b.resolved : null;
                const winTone = win === null || brk === null ? undefined : edgeTone(win - brk, b.price_z);
                return (
                  <tr key={b.bucket_bp} className="border-t border-[var(--color-border)]">
                    <td className="py-1 num">
                      {b.bucket}–{((b.bucket_bp + 500) / 10000).toFixed(2)}
                    </td>
                    <td className="py-1">
                      <div className="flex items-center gap-2">
                        {/* Полоса — одна серия (число входов), цвет — тон корзины
                            с текстовой пометкой рядом: цвет не единственный носитель. */}
                        <div
                          className="h-2 rounded-sm"
                          style={{
                            width: `${Math.max(4, Math.round((100 * b.entries) / max))}px`,
                            background: toneColor(tone) ?? "var(--color-muted)",
                          }}
                        />
                        <span className="num">{b.entries}</span>
                      </div>
                    </td>
                    <td className="py-1 text-right num" style={{ color: toneColor(winTone) }}>
                      {b.resolved > 0 ? `${b.won}/${b.resolved} · ${ratio(b.won, b.resolved)}` : "—"}
                      <Mark tone={winTone} />
                    </td>
                    <td className="py-1 text-right num" title="средняя цена входа: доля угаданного, при которой счёт нулевой">
                      {meanPrice(b.price_sum, b.resolved)}
                    </td>
                    <td className="py-1 text-right num" style={{ color: toneColor(edgeTone(b.price_z, b.price_z)) }}>
                      {zWord(b.price_z)}
                    </td>
                    <td className="py-1 text-right num" style={{ color: toneColor(feeTone(b.fees, b.cost)) }}>
                      {usdCents(b.fees)}
                    </td>
                    <td className="py-1 text-right num" style={{ color: toneColor(tone) }}>
                      {signedUsd(b.pnl)}
                      <Mark tone={tone} />
                    </td>
                    <td className="py-1 text-right num" style={{ color: toneColor(tone) }}>
                      {roiWord(b.roi)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

/** (5) Итог с комиссией: винрейт, комиссия, счёт, ROI — и z, без которого их нельзя читать. */
function Outcome({ r }: { r: Report }) {
  const pnl = Number(r.pnl);
  const tone = edgeTone(pnl, r.price_z);
  const win = r.resolved > 0 ? r.won / r.resolved : null;
  const brk = r.resolved > 0 ? Number(r.price_sum) / r.resolved : null;
  const winTone = win === null || brk === null ? undefined : edgeTone(win - brk, r.price_z);
  const fTone = feeTone(r.fees, r.cost);
  return (
    <Section label="итог тейкера с комиссией">
      {r.resolved === 0 ? (
        <Empty>сведённых приценённых строк ещё нет</Empty>
      ) : (
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <Tile
            label="винрейт"
            value={`${r.won}/${r.resolved} · ${ratio(r.won, r.resolved)}`}
            sub={`безубыток ${meanPrice(r.price_sum, r.resolved)}`}
            tone={winTone}
          />
          <Tile label="против цены" value={zWord(r.price_z)} sub="порог |z| ≈ 3.2 на 36 гипотез" tone={edgeTone(r.price_z, r.price_z)} />
          <Tile label="потрачено" value={usd(r.cost)} sub={`${r.pnl_known} строк со счётом`} tone={r.pnl_known > 0 ? "good" : undefined} />
          <Tile label="комиссия" value={usdCents(r.fees)} sub="тейкерская, учтена в счёте" tone={fTone} />
          <Tile label="счёт" value={signedUsd(r.pnl)} sub="выплата − вход − комиссия" tone={tone} />
          <Tile label="ROI" value={roiWord(r.roi)} sub="счёт к потраченному" tone={tone} />
        </dl>
      )}
      <p className="mt-2 text-[10px] text-[var(--color-muted)]">
        зелёный — плюс при z ≥ 2, красный — минус при z ≤ −2, жёлтый — знак без значимости;
        доли воронки: ≥ 50 % зелёные, &lt; 20 % красные; комиссия: ≤ 1.5 % потраченного зелёная.
      </p>
    </Section>
  );
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub: string; tone: Tone | undefined }) {
  return (
    <div className="rounded border border-[var(--color-border)] p-2">
      <dt className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">{label}</dt>
      <dd className="num text-lg" style={{ color: toneColor(tone) }}>
        {value}
        <Mark tone={tone} />
      </dd>
      <dd className="text-[10px] text-[var(--color-muted)]">{sub}</dd>
    </div>
  );
}

/**
 * Блок отчёта: подпись и тело, ВСЕГДА раскрытое и в основном цвете текста.
 *
 * Не Note: та — раскрывашка для пояснений, свёрнутая по умолчанию и серая
 * внутри. Здесь под подписью лежат сами числа, ради которых раздел открывают,
 * и прятать их за клик или гасить серым значило бы показать экран «данных нет»
 * при полной воронке — ровно так оператор его и прочитал 2026-09-03.
 */
function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="rounded border border-[var(--color-border)] p-3">
      <h3 className="mb-2 text-[10px] uppercase tracking-wide text-[var(--color-muted)]">{label}</h3>
      <div className="text-xs leading-relaxed text-[var(--color-fg)]">{children}</div>
    </section>
  );
}

/** Текстовая пометка тона — цвет не единственный носитель смысла. */
function Mark({ tone }: { tone: Tone | undefined }) {
  if (!tone) return null;
  const glyph = tone === "good" ? "▲" : tone === "bad" ? "▼" : "·";
  const title = tone === "good" ? "значимо в плюс" : tone === "bad" ? "значимо в минус" : "незначимо";
  return (
    <span className="ml-1 text-[10px] align-middle" title={title} aria-label={title}>
      {glyph}
    </span>
  );
}
